import React from 'react';
import s from './s.module.less';
import dayjs from 'utils/dayjs';
import { Button, Dropdown, Select } from 'antd';
import { EBillingStatus, EBillingStatusLabel, EChargeType, EChargeTypeLabel, TPaymentBilling } from 'types/provider';
import usePatientPaymentBillingList from 'hooks/usePatientPaymentBillingList';
import StatusLabel from 'components/ManageSubscription/components/StatusLabel';
import { getChargeTypeLabel } from 'utils/common';
import PaymentEventModal from 'components/ManageSubscription/components/PaymentEventModal';
import { TGetPaymentBillingList } from 'api/provider';
import DataUpdatedFlagStore from 'store/DataUpdatedFlag';
import InvoiceDetailModal from 'components/ManageSubscription/components/PaymentTable/components/InvoiceDetailModal';
import RefundModal, { canIssueRefund } from 'components/RefundModal';

interface Props {
    clientId?: number;
    isGroupPractice?: boolean;
}

const Billings = ({ clientId, isGroupPractice }: Props) => {
    const [currentItem, setCurrentItem] = React.useState<TPaymentBilling>();
    const [showViewTransactionModal, setShowViewTransactionModal] = React.useState(false);
    const [showIssueRefundModal, setShowIssueRefundModal] = React.useState(false);
    const [showInvoiceDetailModal, setShowInvoiceDetailModal] = React.useState(false);

    const [chargeType, setChargeType] = React.useState<EChargeType | undefined>(undefined);
    const [billingStatus, setBillingStatus] = React.useState<EBillingStatus | undefined>(undefined);
    const [paymentBillingList, paymentBillingListTotal, loading, refetch] = usePatientPaymentBillingList(
        clientId
            ? {
                  clientId,
                  size: 500,
              }
            : {
                  size: 500,
              },
        isGroupPractice,
    );

    const [getDataUpdatedFlag] = DataUpdatedFlagStore.useStore();
    const billingDataUpdatedFlag = getDataUpdatedFlag('billingDataUpdated');

    React.useEffect(() => {
        if (billingDataUpdatedFlag) {
            refetch(
                clientId
                    ? {
                          clientId,
                          size: 500,
                      }
                    : {
                          size: 500,
                      },
            );
        }
    }, [billingDataUpdatedFlag, clientId]);

    const refetchData = () => {
        const filter: Partial<TGetPaymentBillingList> = {};
        filter.current = 1;
        filter.size = 500;
        if (clientId) {
            filter.clientId = clientId;
        }
        if (chargeType) {
            filter.chargeType = [chargeType];
        }
        if (billingStatus) {
            filter.billingStatus = [billingStatus];
        }
        refetch(filter);
    };

    React.useEffect(() => {
        refetchData();
    }, [chargeType, billingStatus]);

    const ChargeTypeOptions = [
        //     text: EBillingStatusLabel.PAID,
        //     value: EBillingStatus.PAID,
        // },
        {
            text: EBillingStatusLabel.FAILED,
            value: EBillingStatus.FAILED,
        },
        {
            text: EBillingStatusLabel.CANCELED,
            value: EBillingStatus.CANCELED,
        },
        // {
        //     text: EBillingStatusLabel.NOT_READY,
        //     value: EBillingStatus.NOT_READY,
        // },
        {
            text: EBillingStatusLabel.PROCESSING,
            value: EBillingStatus.PROCESSING,
        },
        {
            text: EBillingStatusLabel.SUCCEEDED,
            value: EBillingStatus.SUCCEEDED,
        },
        {
            text: EBillingStatusLabel.REFUND_PROCESSING,
            value: EBillingStatus.REFUND_PROCESSING,
        },
        {
            text: EBillingStatusLabel.REFUNDED,
            value: EBillingStatus.REFUNDED,
        },
        {
            text: EBillingStatusLabel.REFUND_FAILED,
            value: EBillingStatus.REFUND_FAILED,
        },
        {
            text: EBillingStatusLabel.DISPUTE_PROCESSING,
            value: EBillingStatus.DISPUTE_PROCESSING,
        },
        {
            text: EBillingStatusLabel.DISPUTE_WON,
            value: EBillingStatus.DISPUTE_WON,
        },
        {
            text: EBillingStatusLabel.DISPUTE_LOST,
            value: EBillingStatus.DISPUTE_LOST,
        },
        {
            text: EBillingStatusLabel.PATIENT_NOT_READY,
            value: EBillingStatus.PATIENT_NOT_READY,
        },
        {
            text: EBillingStatusLabel.PROVIDER_NOT_READY,
            value: EBillingStatus.PROVIDER_NOT_READY,
        },
    ];

    const BillingStatusOptions = [
        {
            text: EBillingStatusLabel.DISPUTE_OPEN,
            value: EBillingStatus.DISPUTE_OPEN,
        },
        // {
        //     text: EBillingStatusLabel.DISPUTE_CLOSED,
        //     value: EBillingStatus.DISPUTE_CLOSED,
        // },
        {
            text: EBillingStatusLabel.PENDING,
            value: EBillingStatus.PENDING,
        },

        // {
        //     text: EBillingStatusLabel.PAID,
        //     value: EBillingStatus.PAID,
        // },
        {
            text: EBillingStatusLabel.FAILED,
            value: EBillingStatus.FAILED,
        },
        {
            text: EBillingStatusLabel.CANCELED,
            value: EBillingStatus.CANCELED,
        },
        // {
        //     text: EBillingStatusLabel.NOT_READY,
        //     value: EBillingStatus.NOT_READY,
        // },
        {
            text: EBillingStatusLabel.PROCESSING,
            value: EBillingStatus.PROCESSING,
        },
        {
            text: EBillingStatusLabel.SUCCEEDED,
            value: EBillingStatus.SUCCEEDED,
        },
        {
            text: EBillingStatusLabel.REFUND_PROCESSING,
            value: EBillingStatus.REFUND_PROCESSING,
        },
        {
            text: EBillingStatusLabel.REFUNDED,
            value: EBillingStatus.REFUNDED,
        },
        {
            text: EBillingStatusLabel.REFUND_FAILED,
            value: EBillingStatus.REFUND_FAILED,
        },
        {
            text: EBillingStatusLabel.DISPUTE_PROCESSING,
            value: EBillingStatus.DISPUTE_PROCESSING,
        },
        {
            text: EBillingStatusLabel.DISPUTE_WON,
            value: EBillingStatus.DISPUTE_WON,
        },
        {
            text: EBillingStatusLabel.DISPUTE_LOST,
            value: EBillingStatus.DISPUTE_LOST,
        },
        {
            text: EBillingStatusLabel.PATIENT_NOT_READY,
            value: EBillingStatus.PATIENT_NOT_READY,
        },
        {
            text: EBillingStatusLabel.PROVIDER_NOT_READY,
            value: EBillingStatus.PROVIDER_NOT_READY,
        },
    ];

    return (
        <div className={s.wrap}>
            <div className={s.filter}>
                <Select
                    options={ChargeTypeOptions}
                    value={chargeType}
                    onChange={(value) => setChargeType(value)}
                    placeholder="Charge type"
                    dropdownStyle={{ minWidth: '160px' }}
                />
                <Select
                    options={BillingStatusOptions}
                    value={billingStatus}
                    onChange={(value) => setBillingStatus(value)}
                    placeholder="Status"
                    dropdownStyle={{ minWidth: '160px' }}
                />
            </div>
            {paymentBillingList && paymentBillingList.length > 0 ? (
                paymentBillingList.map((item) => {
                    const showIssueRefund = canIssueRefund(item);
                    const showView =
                        item.billingStatus !== EBillingStatus.PROVIDER_NOT_READY &&
                        item.billingStatus !== EBillingStatus.PATIENT_NOT_READY;
                    const items: any = [
                        {
                            key: 'refund',
                            label: (
                                <div
                                    onClick={() => {
                                        setCurrentItem(item);
                                        setShowIssueRefundModal(true);
                                    }}
                                    className={s.popItem}
                                >
                                    Issue a refund
                                </div>
                            ),
                        },
                    ].filter(Boolean);

                    return (
                        <div key={item.id} className={s.paymentItem}>
                            <div className="flex flex-col gap-1 items-start">
                                <StatusLabel
                                    value={item.billingStatus}
                                    isTextVisit={item.chargeType === EChargeType.TEXT_VISIT}
                                    billingId={item.id}
                                    item={item}
                                    isInvoice={item.chargeType === EChargeType.INVOICE}
                                />
                                <div className="font-medium">{getChargeTypeLabel(item.chargeType)}</div>
                                <div className="text-gray-500 font-medium text-xs">
                                    Patient charge:{' '}
                                    <span className="font-bold">${((item.amountCents || 0) / 100).toFixed(2)}</span>
                                </div>
                                <div className="text-gray-500 font-medium text-xs">
                                    Payout: <span className="font-bold">${(item.payoutAmount || 0).toFixed(2)}</span>
                                </div>
                            </div>
                            <div className="flex flex-col gap-1 items-end">
                                <div className="text-gray-500">{dayjs(item.createdAt).format('MM/DD/YYYY')}</div>
                                <div className={s.actions}>
                                    <Button
                                        disabled={!showView}
                                        onClick={() => {
                                            if (item.chargeType === EChargeType.INVOICE) {
                                                setCurrentItem(item);
                                                setShowInvoiceDetailModal(true);
                                            } else {
                                                setCurrentItem(item);
                                                setShowViewTransactionModal(true);
                                            }
                                        }}
                                        className={s.viewButton}
                                    >
                                        View
                                    </Button>
                                    {showIssueRefund && (
                                        <Dropdown
                                            menu={{
                                                items,
                                            }}
                                            trigger={['hover']}
                                        >
                                            <Button className={s.moreButton}>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="20"
                                                    height="20"
                                                    viewBox="0 0 20 20"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M6 10C6 11.1046 5.10457 12 4 12C2.89543 12 2 11.1046 2 10C2 8.89543 2.89543 8 4 8C5.10457 8 6 8.89543 6 10Z"
                                                        fill="#374151"
                                                    />
                                                    <path
                                                        d="M12 10C12 11.1046 11.1046 12 10 12C8.89543 12 8 11.1046 8 10C8 8.89543 8.89543 8 10 8C11.1046 8 12 8.89543 12 10Z"
                                                        fill="#374151"
                                                    />
                                                    <path
                                                        d="M16 12C17.1046 12 18 11.1046 18 10C18 8.89543 17.1046 8 16 8C14.8954 8 14 8.89543 14 10C14 11.1046 14.8954 12 16 12Z"
                                                        fill="#374151"
                                                    />
                                                </svg>
                                            </Button>
                                        </Dropdown>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })
            ) : (
                <div className={s.empty}>No billing records</div>
            )}
            <RefundModal
                open={showIssueRefundModal}
                item={currentItem}
                isGroupPractice={isGroupPractice}
                onCancel={() => {
                    setShowIssueRefundModal(false);
                    setCurrentItem(undefined);
                }}
                onSuccess={() => {
                    setShowIssueRefundModal(false);
                    setCurrentItem(undefined);
                    refetchData();
                }}
            />
            <PaymentEventModal
                open={showViewTransactionModal}
                onCancel={() => {
                    setShowViewTransactionModal(false);
                }}
                item={currentItem}
                isGroupPractice={isGroupPractice}
            />
            <InvoiceDetailModal
                open={showInvoiceDetailModal}
                onCancel={() => {
                    setShowInvoiceDetailModal(false);
                }}
                item={currentItem}
                refetchData={refetchData}
            />
        </div>
    );
};

export default Billings;
